import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  children?: ReactNode;
}

/**
 * Beschriftetes Eingabefeld.
 *
 * Label, Hinweis und Fehlermeldung sind ueber generierte IDs mit dem Feld
 * verknuepft (aria-describedby), damit Screenreader sie gemeinsam vorlesen.
 * Der Fehlertext beginnt mit "Fehler:" — die rote Farbe allein darf die
 * Information nicht tragen.
 */
export function Field({ label, hint, error, children, ...rest }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children ? (
        children
      ) : (
        <input
          {...rest}
          id={id}
          className="input"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        />
      )}
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          Fehler: {error}
        </span>
      )}
    </div>
  );
}
